import type { ICameraVideoTrack, IMicrophoneAudioTrack } from 'agora-rtc-react';
import AgoraRTC from 'agora-rtc-react';
import type { VideoInput } from '../video/videoHelpers';

import { StudioLogger } from '@studio/logger/StudioLogger';

const LOG = new StudioLogger('agoraDeviceListeners');

/* eslint-disable no-unused-vars */
interface DispatchFunction {
  (action: any): void;
}

/* eslint-disable no-unused-vars */
interface Actions {
  handleVideoInputChanged: (...args: any[]) => any;
  handleAudioInputChanged: (...args: any[]) => any;
  showNotification: (...args: any[]) => any;
  addToAttendeeTiles: (...args: any[]) => any;
}

/* eslint-disable no-unused-vars */
export interface VideoActions {
  showNotification: (...args: any[]) => any;
  addToAttendeeTiles: (...args: any[]) => any;
  setAttendeeCameraStatus: (...args: any[]) => any;
  setVideoInputDevice: (...args: any[]) => any;
}

/**
 * Handles camera device changes (connection/disconnection) and manages video input state accordingly.
 *
 * When a camera is disconnected and no other cameras are available, disables the attendee's camera.
 * When a camera is disconnected but alternatives exist, switches to the default camera.
 * When a new camera is connected, switches to the newly connected camera.
 *
 * @param currentVideoInput - The current video input object containing the video track
 * @param changedDevice - The device change event object from Agora containing device state and info
 * @param dispatch - Redux dispatch function to trigger state updates
 * @param actions - Object containing action creators for video-related state changes
 * @param attendeeId - The unique identifier for the attendee whose camera is being managed
 * @returns Promise<VideoInput> - The updated video input object after handling the device change
 *
 * @example
 * ```typescript
 * const updatedVideoInput = await handleCameraDeviceChange(
 *   currentVideoInput,
 *   { state: 'INACTIVE', device: { deviceId: 'cam1', label: 'Camera 1' } },
 *   dispatch,
 *   { setAttendeeCameraStatus, addToAttendeeTiles, setVideoInputDevice },
 *   'attendee-123'
 * );
 * ```
 */
async function handleCameraDeviceChange(
  currentVideoInput: VideoInput,
  changedDevice: any,
  dispatch: DispatchFunction,
  actions: VideoActions,
  attendeeId: string
): Promise<VideoInput> {
  if (!currentVideoInput.videoTrack) return currentVideoInput;

  try {
    const changedDeviceLabel = changedDevice.device.label;
    const isDisconnectedDeviceCurrent = currentVideoInput.device === changedDevice.device.deviceId;
    const currentDeviceLabel = currentVideoInput.videoTrack.getTrackLabel();

    LOG.info(`Camera device change detected: ${changedDeviceLabel} is now ${changedDevice.state}`, {
      changedDevice,
      isDisconnectedDeviceCurrent
    });
    const isDeviceDisconnected = isDisconnectedDeviceCurrent && changedDevice.state === 'INACTIVE';
    const isDeviceConnected = changedDevice.state === 'ACTIVE';
    const devices = await AgoraRTC.getCameras();
    const defaultVideoDevice = devices[0]?.deviceId;
    const defaultVideoLabel = devices[0]?.label;
    const newDeviceId = changedDevice.device.deviceId;
    // if the current device is unplugged and there are no other devices available, we need to toggle the camera off since there is no longer an active device

    if (isDeviceDisconnected && devices.length === 0) {
      LOG.info(`Camera ${changedDeviceLabel} disconnected. No other cameras available, toggling camera off.`);
      dispatch(actions.setAttendeeCameraStatus(attendeeId, false));
      const _videoInput: VideoInput = {
        ...currentVideoInput,
        device: ''
      };
      dispatch(actions.setVideoInputDevice(_videoInput));
      return _videoInput;
    }

    // if the current device is inactive but there are other devices available, we can attempt to switch to the default device
    if (isDeviceDisconnected && defaultVideoDevice) {
      LOG.info(`Camera ${changedDeviceLabel} disconnected. Changing to default device - ${defaultVideoLabel}.`);
      const _videoInput: VideoInput = {
        ...currentVideoInput,
        device: defaultVideoDevice
      };
      await _videoInput.videoTrack?.setDevice(defaultVideoDevice);
      dispatch(actions.setAttendeeCameraStatus(attendeeId, true));
      // To be fixed with WEBINAR-60948 - need to wait for the track to update before adding to tiles,
      // otherwise it may cause a black screen or the filter not used due to the track not being ready
      await new Promise(resolve => setTimeout(resolve, 300));
      dispatch(actions.addToAttendeeTiles(attendeeId, _videoInput.videoTrack));
      dispatch(actions.setVideoInputDevice(_videoInput));
      dispatch(
        actions.showNotification(
          `Camera ${changedDeviceLabel} disconnected. Switching from ${changedDeviceLabel} to ${defaultVideoLabel}`,
          {
            timeout: 5000,
            type: 'alert'
          }
        )
      );
      return _videoInput;
    }

    // if a new device is connected, attempt to switch to it
    if (newDeviceId && devices.length > 0 && isDeviceConnected) {
      LOG.info(`New camera detected. Switching from ${currentDeviceLabel} to ${changedDeviceLabel}`);
      const _videoInput: VideoInput = {
        ...currentVideoInput,
        device: newDeviceId
      };
      await _videoInput.videoTrack?.setDevice(newDeviceId);
      // To be fixed with WEBINAR-60948 - need to wait for the track to update before adding to tiles,
      // otherwise it may cause a black screen or the filter not used due to the track not being ready
      await new Promise(resolve => setTimeout(resolve, 300));
      dispatch(actions.addToAttendeeTiles(attendeeId, _videoInput.videoTrack));
      dispatch(actions.setAttendeeCameraStatus(attendeeId, true));
      dispatch(actions.setVideoInputDevice(_videoInput));
      dispatch(
        actions.showNotification(`New Camera detected. Switching from ${currentDeviceLabel} to ${changedDeviceLabel}`, {
          timeout: 5000,
          type: 'alert'
        })
      );
      return _videoInput;
    }

    return currentVideoInput;
  } catch (error) {
    LOG.error('Error handling camera device change:', error);
    return currentVideoInput;
  }
}

function setupVideoDeviceListeners(
  videoInput: VideoInput,
  dispatch: DispatchFunction,
  actions: VideoActions,
  attendeeId: string
) {
  LOG.info('Setting up video device listeners for camera changes');
  let currentVideoInput = videoInput;

  AgoraRTC.on('camera-changed', async (changedDevice: any) => {
    currentVideoInput = await handleCameraDeviceChange(currentVideoInput, changedDevice, dispatch, actions, attendeeId);
  });
}

function clearVideoDeviceListeners() {
  LOG.info('Clearing up video device listeners for camera changes');
  AgoraRTC.removeAllListeners('camera-changed');
}

function setupAudioDeviceListeners(
  localAudioTrack: IMicrophoneAudioTrack | null,
  dispatch: DispatchFunction,
  actions: Actions
) {
  if (!localAudioTrack) {
    return;
  }

  AgoraRTC.on('microphone-changed', async (changedDevice: any) => {
    await handleDeviceChange(
      'audio',
      localAudioTrack,
      changedDevice,
      dispatch,
      actions.handleAudioInputChanged,
      actions.showNotification
    );
  });
}

function setupPlaybackDeviceListener() {
  AgoraRTC.on('playback-device-changed', (info: any) => {
    LOG.info(`Playback device changed: ${JSON.stringify(info)}`);
  });
}

async function handleDeviceChange(
  deviceType: 'audio' | 'video',
  deviceTrack: IMicrophoneAudioTrack | ICameraVideoTrack,
  changedDevice: any,
  dispatch: DispatchFunction,
  handleDevice: Function,
  showNotification: Function
) {
  let switchDevice = false;
  const deviceNaming = deviceType === 'audio' ? 'microphone' : 'camera';
  const currentDeviceLabel = deviceTrack.getTrackLabel();

  LOG.info(`Device Change Detected for ${deviceNaming} ${changedDevice.device.label}`, {
    changedDevice
  });

  // When a new device is detected, switch to the new device
  if (changedDevice.state === 'ACTIVE') {
    try {
      await deviceTrack.setDevice(changedDevice.device.deviceId);
      dispatch(handleDevice(changedDevice.device.deviceId));
      switchDevice = true;
      LOG.info(`New ${deviceNaming} detected. Switching from ${currentDeviceLabel} to ${changedDevice.device.label}`);

      // show notification only if the device label is different aka switching to a brand new device
      if (currentDeviceLabel !== changedDevice.device.label) {
        dispatch(
          showNotification(
            `New ${deviceNaming} detected. Switching from ${currentDeviceLabel} to ${changedDevice.device.label}`,
            {
              timeout: 5000,
              type: 'alert'
            }
          )
        );
      }
    } catch (error) {
      LOG.error(`Error switching to new ${deviceNaming} device: ${error}`);
    }
  } else if (changedDevice.device.label === currentDeviceLabel) {
    let oldDevices;

    if (deviceType === 'audio') {
      oldDevices = await AgoraRTC.getMicrophones();
    } else {
      oldDevices = await AgoraRTC.getCameras();
    }

    // Switch to an existing device when the unplugged device is the current device
    if (oldDevices[0]) {
      await deviceTrack.setDevice(oldDevices[0].deviceId);
      dispatch(handleDevice(oldDevices[0].deviceId));
      switchDevice = true;

      LOG.info(
        `${deviceNaming} ${changedDevice.device.label} disconnected. Changing to default ${oldDevices[0].label}.`
      );
      dispatch(
        showNotification(`Your ${deviceNaming} ${changedDevice.device.label} disconnected. Just changed to default.`, {
          timeout: 5000,
          type: 'alert'
        })
      );
    }
  }
  return switchDevice;
}

export {
  setupAudioDeviceListeners,
  setupVideoDeviceListeners,
  setupPlaybackDeviceListener,
  handleDeviceChange,
  handleCameraDeviceChange,
  clearVideoDeviceListeners
};
